import React, {FC, useEffect, useMemo, useState} from "react";
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    Divider,
    Heading,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    Radio,
    RadioGroup,
    Stack,
    Text,
    useToast,
} from "@chakra-ui/react";
import {Icon, ViewIcon, ViewOffIcon} from "@chakra-ui/icons";
import {FiX} from "react-icons/fi";
import {useDispatch} from "react-redux";
import {Breadcrumbs} from "../../components/Breadcrumbs/Breadcrumbs";
import {BreadcrumbsData} from "../../shared/entities/Breadcrumbs/BreadCrumbsData";
import {setBreadcrumbsLinks} from "../../redux/Breadcrumbs/BreadcrumbsReducer";
import {
    ApplicationPaymentAdminUpdateDto,
    ApplicationSettingsRepositoryInstance,
    SmtpMailAdminUpdateDto,
} from "../../repository/Application/ApplicationSettingsRepository";
import {User} from "../../shared/entities/Users/User";
import {loadPaymentClientContext} from "../../service/Payment/PaymentConfigLoader";
import {UserRepositoryInstance} from "../../repository/User/UserRepository";
import {InactiveOrderReminderSettingsSection} from "../../components/MailTemplateEditor";

/** Как у фильтров в Order History (dealership): высота, шрифт, белый фон */
const filterControlProps = {
    h: "32px",
    minH: "32px",
    size: "sm" as const,
    borderRadius: "md",
    fontSize: "xs",
    lineHeight: "1.25rem",
    borderColor: "gray.200",
    bg: "white",
    _hover: {borderColor: "gray.300"},
    _focusVisible: {
        borderColor: "blue.400",
        boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
    },
};

const filterClearIcon = (
    <Icon as={FiX} boxSize={4} strokeWidth={5} color="gray.300" _hover={{color: "gray.500"}}/>
);

const filterClearButtonProps = {
    size: "xs" as const,
    variant: "ghost" as const,
    position: "absolute" as const,
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 1,
    w: "12px",
    h: "12px",
    minH: "12px",
    minW: "12px",
    p: 0,
    borderRadius: "md",
    color: "gray.600",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bg: "transparent",
    _hover: {bg: "transparent", color: "gray.700"},
    _active: {bg: "transparent"},
};

const DEFAULT_STRIPE_PERCENT_FEE = "0.032";
const DEFAULT_STRIPE_FIXED_FEE_USD = "0.30";
const DEFAULT_STRIPE_TOLERANCE_USD = "0.00";

const DEFAULT_MAIL_SMTP_PROTOCOL = "smtps";
const DEFAULT_MAIL_SMTP_PORT = "465";
const DEFAULT_MAIL_SMTP_HOST = "smtp.gmail.com";
const DEFAULT_MAIL_SMTP_USERNAME = "info@akdealerservices.com";

function parseDecimalInput(raw: string): number | null {
    const trimmed = raw.trim().replace(",", ".");
    if (!trimmed) {
        return null;
    }
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
}

/** Доля 0.032 или проценты 3.2 → 0.032 */
function parseStripePercentFeeInput(raw: string): number | null {
    const n = parseDecimalInput(raw);
    if (n === null) {
        return null;
    }
    if (n > 1) {
        return n / 100;
    }
    return n;
}

function userLabel(u: User): string {
    const email = u.email?.trim();
    const login = u.login?.trim();
    if (email && login) return `${email} (${login})`;
    return email || login || String(u.id);
}

export const ApplicationSettings: FC<{ links: Array<BreadcrumbsData> }> = ({links}) => {
    const dispatch = useDispatch();
    const toast = useToast();

    const [loading, setLoading] = useState(true);
    const [savingPayment, setSavingPayment] = useState(false);
    const [savingSmtpMail, setSavingSmtpMail] = useState(false);
    const [testingSmtpMail, setTestingSmtpMail] = useState(false);
    const [showMailSmtpPassword, setShowMailSmtpPassword] = useState(false);

    const [stripePercentFee, setStripePercentFee] = useState(DEFAULT_STRIPE_PERCENT_FEE);
    const [stripeFixedFeeUsd, setStripeFixedFeeUsd] = useState(DEFAULT_STRIPE_FIXED_FEE_USD);
    const [stripePaymentToleranceUsd, setStripePaymentToleranceUsd] = useState(DEFAULT_STRIPE_TOLERANCE_USD);
    const [cardPaymentMode, setCardPaymentMode] = useState<"ALL" | "WHITELIST">("ALL");

    const [mailSmtpProtocol, setMailSmtpProtocol] = useState(DEFAULT_MAIL_SMTP_PROTOCOL);
    const [mailSmtpPort, setMailSmtpPort] = useState(DEFAULT_MAIL_SMTP_PORT);
    const [mailSmtpHost, setMailSmtpHost] = useState(DEFAULT_MAIL_SMTP_HOST);
    const [mailSmtpUsername, setMailSmtpUsername] = useState(DEFAULT_MAIL_SMTP_USERNAME);
    const [mailSmtpPassword, setMailSmtpPassword] = useState("");
    const [mailSmtpPasswordConfigured, setMailSmtpPasswordConfigured] = useState(false);

    const [whitelistPicked, setWhitelistPicked] = useState<User[]>([]);
    const [userSearchInput, setUserSearchInput] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [userSearchResults, setUserSearchResults] = useState<User[]>([]);
    const [userSearchLoading, setUserSearchLoading] = useState(false);

    useEffect(() => {
        setBreadcrumbsLinks(dispatch, links);
    }, [dispatch, links]);

    const pickedIdSet = useMemo(
        () => new Set(whitelistPicked.map((u) => String(u.id))),
        [whitelistPicked],
    );

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedSearch(userSearchInput), 300);
        return () => window.clearTimeout(t);
    }, [userSearchInput]);

    useEffect(() => {
        if (cardPaymentMode !== "WHITELIST") {
            setUserSearchResults([]);
            return;
        }
        const q = debouncedSearch.trim();
        if (q.length < 2) {
            setUserSearchResults([]);
            setUserSearchLoading(false);
            return;
        }
        let cancelled = false;
        setUserSearchLoading(true);
        UserRepositoryInstance.searchUsersForWhitelist(q, 30).then((res) => {
            if (cancelled) return;
            setUserSearchLoading(false);
            if (res.None || !res.Some) {
                setUserSearchResults([]);
                return;
            }
            setUserSearchResults(res.Some.filter((u) => !pickedIdSet.has(String(u.id))));
        });
        return () => {
            cancelled = true;
        };
    }, [debouncedSearch, cardPaymentMode, pickedIdSet]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const adminRes = await ApplicationSettingsRepositoryInstance.getAdmin();
            if (cancelled) return;

            if (!adminRes.None && adminRes.Some && adminRes.Some.stripePercentFee !== undefined) {
                const a = adminRes.Some;
                setStripePercentFee(String(a.stripePercentFee));
                setStripeFixedFeeUsd(String(a.stripeFixedFeeUsd));
                setStripePaymentToleranceUsd(String(a.stripePaymentToleranceUsd));
                setCardPaymentMode(a.cardPaymentMode);
                setMailSmtpProtocol(a.mailSmtpProtocol || DEFAULT_MAIL_SMTP_PROTOCOL);
                setMailSmtpPort(String(a.mailSmtpPort ?? DEFAULT_MAIL_SMTP_PORT));
                setMailSmtpHost(a.mailSmtpHost || DEFAULT_MAIL_SMTP_HOST);
                setMailSmtpUsername(a.mailSmtpUsername || DEFAULT_MAIL_SMTP_USERNAME);
                setMailSmtpPassword("");
                setMailSmtpPasswordConfigured(Boolean(a.mailSmtpPasswordConfigured));
                const ids = (a.allowedUserIds || []).map(Number).filter((n) => Number.isFinite(n));
                if (ids.length > 0) {
                    const ur = await UserRepositoryInstance.getUsersByIds(ids);
                    if (!cancelled && !ur.None && ur.Some) {
                        setWhitelistPicked(ur.Some);
                    }
                } else {
                    setWhitelistPicked([]);
                }
            } else {
                toast({title: "Failed to load settings", status: "error", duration: 4000, isClosable: true});
            }
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addUserToWhitelist = (u: User) => {
        const id = String(u.id);
        if (pickedIdSet.has(id)) return;
        setWhitelistPicked((prev) => [...prev, u]);
        setUserSearchInput("");
        setDebouncedSearch("");
        setUserSearchResults([]);
        setIsUserDropdownOpen(false);
    };

    const removeUserFromWhitelist = (id: string) => {
        setWhitelistPicked((prev) => prev.filter((u) => String(u.id) !== id));
    };

    const handleSavePayment = async () => {
        const pct = parseStripePercentFeeInput(stripePercentFee || DEFAULT_STRIPE_PERCENT_FEE);
        const fixed = parseDecimalInput(stripeFixedFeeUsd || DEFAULT_STRIPE_FIXED_FEE_USD);
        const tol = parseDecimalInput(stripePaymentToleranceUsd ?? DEFAULT_STRIPE_TOLERANCE_USD);
        if (pct === null || pct <= 0 || pct >= 1) {
            toast({
                title: "Invalid Stripe percent fee",
                description: "Use a fraction (e.g. 0.032) or percent (e.g. 3.2 for 3.2%)",
                status: "warning",
            });
            return;
        }
        if (fixed === null || fixed < 0 || tol === null || tol < 0) {
            toast({title: "Invalid Stripe fixed fee or tolerance", status: "warning"});
            return;
        }

        const body: ApplicationPaymentAdminUpdateDto = {
            stripePercentFee: pct,
            stripeFixedFeeUsd: fixed,
            stripePaymentToleranceUsd: tol,
            cardPaymentMode,
            allowedUserIds:
                cardPaymentMode === "WHITELIST"
                    ? whitelistPicked.map((u) => Number(u.id)).filter((n) => Number.isFinite(n))
                    : [],
        };

        setSavingPayment(true);
        const res = await ApplicationSettingsRepositoryInstance.updatePaymentAdmin(body);
        setSavingPayment(false);

        if (res.None) {
            toast({title: "Save failed", status: "error", duration: 4000, isClosable: true});
            return;
        }
        toast({title: "Payment settings saved", status: "success", duration: 3000, isClosable: true});
        await loadPaymentClientContext(dispatch);
    };

    const buildSmtpMailBody = (): SmtpMailAdminUpdateDto | null => {
        const port = parseDecimalInput(mailSmtpPort);
        if (!mailSmtpProtocol.trim() || !mailSmtpHost.trim() || !mailSmtpUsername.trim()) {
            toast({title: "Fill in SMTP protocol, host and username", status: "warning"});
            return null;
        }
        if (port === null || port < 1 || port > 65535 || !Number.isInteger(port)) {
            toast({title: "Invalid SMTP port", status: "warning"});
            return null;
        }
        return {
            protocol: mailSmtpProtocol.trim(),
            port,
            host: mailSmtpHost.trim(),
            username: mailSmtpUsername.trim(),
            ...(mailSmtpPassword.trim() ? {password: mailSmtpPassword.trim()} : {}),
        };
    };

    const handleTestSmtpMail = async () => {
        const body = buildSmtpMailBody();
        if (!body) {
            return;
        }
        if (!mailSmtpPasswordConfigured && !mailSmtpPassword.trim()) {
            toast({title: "Enter SMTP password or save settings first", status: "warning"});
            return;
        }

        setTestingSmtpMail(true);
        const res = await ApplicationSettingsRepositoryInstance.testSmtpMail(body);
        setTestingSmtpMail(false);

        if (res.None || !res.Some) {
            toast({title: "SMTP test failed", status: "error", duration: 5000, isClosable: true});
            return;
        }
        toast({
            title: res.Some.success ? "SMTP connection OK" : "SMTP test failed",
            description: res.Some.message,
            status: res.Some.success ? "success" : "error",
            duration: 6000,
            isClosable: true,
        });
    };

    const handleSaveSmtpMail = async () => {
        const body = buildSmtpMailBody();
        if (!body) {
            return;
        }
        if (!mailSmtpPasswordConfigured && !mailSmtpPassword.trim()) {
            toast({title: "SMTP password is required on first setup", status: "warning"});
            return;
        }

        setSavingSmtpMail(true);
        const res = await ApplicationSettingsRepositoryInstance.updateSmtpMail(body);
        setSavingSmtpMail(false);

        if (res.None) {
            toast({title: "Save failed", status: "error", duration: 4000, isClosable: true});
            return;
        }
        setMailSmtpPassword("");
        setShowMailSmtpPassword(false);
        if (res.Some) {
            setMailSmtpPasswordConfigured(Boolean(res.Some.mailSmtpPasswordConfigured));
        }
        toast({title: "Mail SMTP settings saved", status: "success", duration: 3000, isClosable: true});
    };

    if (loading) {
        return (
            <Box p={6}>
                <Breadcrumbs/>
                <Text mt={4}>Loading...</Text>
            </Box>
        );
    }

    const showUserDropdown =
        cardPaymentMode === "WHITELIST" &&
        isUserDropdownOpen &&
        (userSearchLoading || userSearchResults.length > 0 || debouncedSearch.trim().length >= 2);

    return (
        <Box p={{base: 4, md: 6}} maxW={{base: "900px", xl: "1280px"}}>
            <Breadcrumbs/>

            <Divider mt={{base: 6, md: 8}} mb={3}/>

            <Stack spacing={4}>
                <Heading size="xs" textTransform="uppercase" letterSpacing="wider" color="gray.600">
                    Payment & commission
                </Heading>

                <FormControl>
                    <FormLabel>Stripe Percent Fee (0.032 or 3.2 for 3.2%)</FormLabel>
                    <Input
                        type="number"
                        step="0.000001"
                        value={stripePercentFee}
                        onChange={(e) => setStripePercentFee(e.target.value)}
                    />
                </FormControl>
                <FormControl>
                    <FormLabel>Stripe Fixed Fee (USD)</FormLabel>
                    <Input
                        type="number"
                        step="0.01"
                        value={stripeFixedFeeUsd}
                        onChange={(e) => setStripeFixedFeeUsd(e.target.value)}
                    />
                </FormControl>
                <FormControl>
                    <FormLabel>Payment Tolerance (USD)</FormLabel>
                    <Input
                        type="number"
                        step="0.01"
                        value={stripePaymentToleranceUsd}
                        onChange={(e) => setStripePaymentToleranceUsd(e.target.value)}
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>Card Payment Mode</FormLabel>
                    <RadioGroup
                        onChange={(v) => setCardPaymentMode(v as "ALL" | "WHITELIST")}
                        value={cardPaymentMode}
                    >
                        <Stack direction="column">
                            <Radio value="ALL">All users</Radio>
                            <Radio value="WHITELIST">Whitelist only</Radio>
                        </Stack>
                    </RadioGroup>
                </FormControl>

                {cardPaymentMode === "WHITELIST" && (
                    <FormControl>
                        <FormLabel>Allowed Users</FormLabel>

                        {whitelistPicked.length > 0 && (
                            <Box py={2} display="flex" flexWrap="wrap" maxHeight="160px" overflowY="auto" mb={3}>
                                {whitelistPicked.map((u) => (
                                    <Button
                                        maxHeight={6}
                                        key={String(u.id)}
                                        mr={1}
                                        mb={1}
                                        colorScheme="teal"
                                        variant="solid"
                                        size="sm"
                                        fontSize={12}
                                        onClick={() => removeUserFromWhitelist(String(u.id))}
                                    >
                                        {userLabel(u)} ✕
                                    </Button>
                                ))}
                            </Box>
                        )}

                        <Box position="relative" w="100%">
                            <Input
                                placeholder="Search by login or email"
                                {...filterControlProps}
                                pr="32px"
                                value={userSearchInput}
                                onFocus={() => setIsUserDropdownOpen(true)}
                                onChange={(e) => {
                                    setUserSearchInput(e.target.value);
                                    setIsUserDropdownOpen(true);
                                }}
                                onBlur={() => {
                                    window.setTimeout(() => setIsUserDropdownOpen(false), 150);
                                }}
                            />
                            {userSearchInput && (
                                <IconButton
                                    aria-label="Clear search"
                                    {...filterClearButtonProps}
                                    icon={filterClearIcon}
                                    onClick={() => {
                                        setUserSearchInput("");
                                        setUserSearchResults([]);
                                        setIsUserDropdownOpen(false);
                                    }}
                                />
                            )}
                            {showUserDropdown && (
                                <Box
                                    position="absolute"
                                    top="calc(100% + 4px)"
                                    left={0}
                                    right={0}
                                    zIndex={20}
                                    bg="white"
                                    borderWidth="1px"
                                    borderColor="gray.200"
                                    borderRadius="md"
                                    boxShadow="md"
                                    maxH="50vh"
                                    overflowY="auto"
                                >
                                    {userSearchLoading && (
                                        <Box px={2} py={2} fontSize="xs" color="gray.500">
                                            Searching...
                                        </Box>
                                    )}
                                    {!userSearchLoading &&
                                        userSearchResults.length === 0 &&
                                        debouncedSearch.trim().length >= 2 && (
                                            <Box px={2} py={2} fontSize="xs" color="gray.500">
                                                No results
                                            </Box>
                                        )}
                                    {!userSearchLoading &&
                                        userSearchResults.map((u) => (
                                            <Box
                                                key={String(u.id)}
                                                px={2}
                                                py={1.5}
                                                fontSize="xs"
                                                cursor="pointer"
                                                _hover={{bg: "gray.50"}}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    addUserToWhitelist(u);
                                                }}
                                            >
                                                {userLabel(u)}
                                            </Box>
                                        ))}
                                </Box>
                            )}
                        </Box>
                    </FormControl>
                )}

                <Flex justify="flex-end" pt={1}>
                    <Button
                        size="sm"
                        colorScheme="blue"
                        fontSize="xs"
                        h="32px"
                        minH="32px"
                        px={3}
                        onClick={handleSavePayment}
                        isLoading={savingPayment}
                    >
                        Save payment
                    </Button>
                </Flex>

                <Divider my={3}/>

                <Heading size="xs" textTransform="uppercase" letterSpacing="wider" color="gray.600">
                    Outgoing mail (SMTP)
                </Heading>
                <Text fontSize="xs" color="gray.600">
                    Gmail app password and SMTP settings. Leave password empty to keep the current one.
                </Text>

                <FormControl>
                    <FormLabel>Protocol</FormLabel>
                    <Input value={mailSmtpProtocol} onChange={(e) => setMailSmtpProtocol(e.target.value)}/>
                </FormControl>
                <FormControl>
                    <FormLabel>Port</FormLabel>
                    <Input
                        type="number"
                        value={mailSmtpPort}
                        onChange={(e) => setMailSmtpPort(e.target.value)}
                    />
                </FormControl>
                <FormControl>
                    <FormLabel>Host</FormLabel>
                    <Input value={mailSmtpHost} onChange={(e) => setMailSmtpHost(e.target.value)}/>
                </FormControl>
                <FormControl>
                    <FormLabel>Username (sender email)</FormLabel>
                    <Input value={mailSmtpUsername} onChange={(e) => setMailSmtpUsername(e.target.value)}/>
                </FormControl>
                <FormControl>
                    <FormLabel>
                        Password (Gmail app password)
                        {mailSmtpPasswordConfigured ? " — configured" : " — required"}
                    </FormLabel>
                    <InputGroup>
                        <Input
                            type={showMailSmtpPassword ? "text" : "password"}
                            autoComplete="new-password"
                            pr="2.75rem"
                            placeholder={mailSmtpPasswordConfigured ? "Leave empty to keep current" : "Enter app password"}
                            value={mailSmtpPassword}
                            onChange={(e) => setMailSmtpPassword(e.target.value)}
                        />
                        <InputRightElement h="100%">
                            <IconButton
                                aria-label={showMailSmtpPassword ? "Hide password" : "Show password"}
                                variant="ghost"
                                size="sm"
                                icon={showMailSmtpPassword ? <ViewOffIcon/> : <ViewIcon/>}
                                onClick={() => setShowMailSmtpPassword((prev) => !prev)}
                            />
                        </InputRightElement>
                    </InputGroup>
                </FormControl>

                <Flex justify="flex-end" pt={1} gap={2}>
                    <Button
                        size="sm"
                        variant="outline"
                        fontSize="xs"
                        h="32px"
                        minH="32px"
                        px={3}
                        onClick={handleTestSmtpMail}
                        isLoading={testingSmtpMail}
                    >
                        Test connection
                    </Button>
                    <Button
                        size="sm"
                        colorScheme="blue"
                        fontSize="xs"
                        h="32px"
                        minH="32px"
                        px={3}
                        onClick={handleSaveSmtpMail}
                        isLoading={savingSmtpMail}
                    >
                        Save mail SMTP
                    </Button>
                </Flex>

                <Divider my={3}/>

                <InactiveOrderReminderSettingsSection/>
            </Stack>
        </Box>
    );
};

export default ApplicationSettings;
